import { TColorChip, colorsToBeChosen } from "@elixir/utils";
import { Input } from "antd";
import React from "react";
import { useCreateTagMutation } from "../../../../apis/tryst/create-tag";
import PopoverHeaderWithNav from "../../../../atoms/popover/header-with-nav";
import PrimaryButton from "../../../doctor-tool-button/primary-button";
import { useMyAccessProfileV2 } from "../../../../apis/tryst/get-my-access-profile";
import { TagRecord } from "@elixir/types";

type TColorChipProps = TColorChip & {
  onClick: (colourChip: TColorChip) => void;
  isSelected?: boolean;
};

const ColorChip = ({
  textColor,
  bgColor,
  isSelected = false,
  onClick,
}: TColorChipProps) => {
  return (
    <div
      className={`dr-w-20 dr-h-20 dr-rounded-4 dr-cursor-pointer ${
        isSelected
          ? "dr-border-2 dr-border-doctor-darwin-accent-bright-blue-800"
          : ""
      }`}
      style={{
        backgroundColor: textColor,
      }}
      onClick={() => onClick({ textColor, bgColor })}
    />
  );
};

type TCreateATagComponentProps = {
  handleTagSelect: (selectedTagId: TagRecord["tag_id"]) => void;
  onClose?: () => void;
  onGoBackClick: () => void;
};

const CreateATagComponent = ({
  handleTagSelect,
  onGoBackClick,
  onClose,
}: TCreateATagComponentProps) => {
  const [selectedTagConfig, setSelectedTagConfig] = React.useState<
    {
      name: string;
    } & TColorChip
  >({ name: "", bgColor: "", textColor: "" });

  const handleTagNameChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    const tagName = e.target.value;
    setSelectedTagConfig((currValue) => ({ ...currValue, name: tagName }));
  };

  const handleColourClick = (colorSelected: TColorChip) => {
    setSelectedTagConfig((currValue) => ({ ...currValue, ...colorSelected }));
  };

  const { mutateAsync: createNewTag } = useCreateTagMutation();
  const { data: myAccessProfile } = useMyAccessProfileV2();

  const handleCreateTagClick = async () => {
    if (myAccessProfile?.business?.id) {
      const response = await createNewTag({
        businessId: myAccessProfile?.business?.id || "",
        tagRecord: {
          bg_hex: selectedTagConfig.bgColor,
          hex: selectedTagConfig.textColor,
          label: selectedTagConfig.name,
        },
      });
      if (response.success) {
        handleTagSelect(response?.data?.tag_id!);
        onGoBackClick();
        setSelectedTagConfig({ name: "", bgColor: "", textColor: "" });
      }
    }
  };

  return (
    <>
      <PopoverHeaderWithNav
        title="Create a tag"
        onClose={onClose}
        onGoBack={onGoBackClick}
      />
      <div className="dr-flex-col space-y-16 dr-p-16">
        <div className="dr-space-y-12">
          <div className="TouchBodyRegular">Tag Name *</div>
          <div>
            <Input
              className="dr-rounded-4 dr-p-12 dr-h-40"
              value={selectedTagConfig.name}
              onChange={handleTagNameChange}
            />
          </div>
        </div>
        <div className="dr-space-y-12">
          <div className="TouchBodyRegular">Select Colour * </div>
          <div className="dr-flex dr-space-x-16 dr-py-12">
            {colorsToBeChosen.map((color) => (
              <ColorChip
                key={color.bgColor}
                bgColor={color.bgColor}
                textColor={color.textColor}
                isSelected={color.textColor === selectedTagConfig.textColor}
                onClick={handleColourClick}
              />
            ))}
          </div>
        </div>
        <PrimaryButton
          title="Done"
          onClick={handleCreateTagClick}
          className="dr-w-full"
          isDisabled={
            !selectedTagConfig.name ||
            !selectedTagConfig.bgColor ||
            !selectedTagConfig.textColor
          }
        />
      </div>
    </>
  );
};

export default CreateATagComponent;
